/* eslint-disable max-lines */
import {
  type ListItemType,
  Dialog,
  DialogType,
  ProgressBarState,
} from '@kluein/klue-ui';
import {
  CalendarBlank,
  ArrowSquareIn,
  EnvelopeSimple,
  Info,
  PencilSimple,
  StackSimple,
  Eye,
  Copy,
  CopySimple,
  ArrowsLeftRight,
  Link,
  Code,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CardScheduleModal } from 'components/common/card-schedule-modal';
import useUpdateVisibilityGroups from 'components/common/klueCard/hooks/use-update-visibility-groups/useUpdateVisibilityGroups';
import { getVisibleToValue } from 'components/common/klueCard/partials/klue-card-visibility-groups/helpers';
import { useAuth } from 'contexts/auth';
import { useCardDndToggleContext } from 'hooks/use-card-dnd-toggle/useCardDndToggle';
import { useCardViewer } from 'hooks/use-card-viewer';
import { NotifierType, useNotifier } from 'hooks/use-notifier';
import { useRedirect } from 'hooks/use-redirect';
import useVisibilityGroups from 'hooks/use-visibility-groups/useVisibilityGroups';
import { AnalyticsAction, type AnalyticsActionType } from 'lib/analytics';
import { useDeleteCardMutation } from 'store/api/cards/cards.api';

import { useCardInteractionLog } from '../../hooks';
import { useKlueCardMenu } from '../../hooks/use-klue-card-menu';
import { AddToDigestModal } from '../klue-card-add-to-digest-modal';
import KlueCardAssignToBattlecardModal from '../klue-card-assign-to-battlecard-modal/KlueCardAssignToBattlcardModal';
import { CopyToBoardsModal } from '../klue-card-copy-to-boards-modal';
import KlueCardMoveModal from '../klue-card-move-modal/KlueCardMoveModal';
import { KlueCardSourcesModal } from '../klue-card-sources-modal';
import KlueCardVisibilityGroupsModal from '../klue-card-visibility-groups/KlueCardVisibilityGroupsModal';

import type {
  KlueCardMenuItemProps,
  KlueCardMenuProps,
  KlueCardSnowPlowLabelType,
} from './KlueCardMenu.types';
import type { GetCardPermissionCardsDataResult } from 'pages/profile/partials/sideAction/bulkEdit/cardPermissions/CardPermissions.utils';
import type { Dispatch, RootState } from 'store/store.types';

// Prefetch guard for AI Digest picker warmup. Any board can render
// dozens of KlueCardMenu instances; we only want the first eligible
// card to trigger the digest-list fetch, and only once per page-load.
//
// `inFlight` dedupes concurrent mounts against a live request.
// `attempted` becomes true once ANY outcome (success OR failure) has
// landed — success is inferred from the Rematch store having entries;
// failure is invisible from here since `fetchAllDigests` swallows
// errors. Latching `attempted` on failure prevents the request from
// re-firing on every subsequent mount forever with no backoff. Recovery
// path: page reload (this module-level state is scoped to page-load,
// same as the existing dedupe).
let aiDigestPrefetchInFlight = false;
let aiDigestPrefetchAttempted = false;

function KlueCardMenu(props: KlueCardMenuProps) {
  const {
    sourcesCount,
    cardId,
    profileId,
    children,
    trackingIds,
    isEditable = false,
    hideOpenCard = false,
    hideCardSources = false,
    rank,
  } = props;
  const { redirectToCardEditor } = useRedirect();
  const { t } = useTranslation();
  const { open } = useCardViewer();
  const {
    card,
    cardFromCardsStore,
    view,
    handleCopyEmbed,
    handleCopyLink,
    handleCopyToBoards,
    handleDuplicateCard,
    handleMoveCard,
    sourceRivalId,
    sourceRivalName,
  } = useKlueCardMenu({ cardId, profileId });
  const { isCurator, isNewCardEditorEnabled, isCopyToBoardsEnabled } =
    useAuth();

  const [isDeletingCard, setIsDeletingCard] = useState(false);
  const [isMovingCard, setIsMovingCard] = useState(false);
  const [isCopyingToBoards, setIsCopyingToBoards] = useState(false);
  const [isEditingSources, setIsEditingSources] = useState(false);
  const [isAssigningToBattlecards, setIsAssigningToBattlecards] =
    useState(false);
  const [isAssigningCardPermissions, setIsAssigningCardPermissions] =
    useState(false);
  const [isSchedulingUpdate, setIsSchedulingUpdate] = useState(false);
  const [isAddingToDigest, setIsAddingToDigest] = useState(false);

  // Check schedule from card data in Redux store
  const hasSchedule = !!(card && 'cardSchedule' in card && card.cardSchedule);

  // Warm the Rematch digest list before the curator opens the modal.
  // Same fetch (`filter=draft` via intelDigest.fetchAllDigests) that
  // Alerts fires on its route mount — mirrors what makes the Alerts
  // "Assign to Digests" picker feel instant. Deduped across all cards
  // on the board via module-level guard, and skipped entirely when
  // state is already warm (e.g. user visited Alerts first).
  const menuDispatch = useDispatch<Dispatch>();
  const digestListReady = useSelector<RootState, boolean>(
    (state) => state.intelDigest.allDigests.length > 0,
  );
  useEffect(() => {
    if (!isCurator) return;
    if (
      digestListReady ||
      aiDigestPrefetchInFlight ||
      aiDigestPrefetchAttempted
    )
      return;
    aiDigestPrefetchInFlight = true;
    menuDispatch.intelDigest.fetchAllDigests().finally(() => {
      aiDigestPrefetchInFlight = false;
      aiDigestPrefetchAttempted = true;
    });
  }, [isCurator, digestListReady, menuDispatch]);

  const {
    visibleTo,
    setVisibleTo,
    setSelectGroups,
    selectedGroups,
    visibilityGroups: allVisibilityGroups,
    getCardPermissionCardsData,
  } = useVisibilityGroups();
  const { onVisibilityGroupSelected } = useUpdateVisibilityGroups();

  const handleDoneVisibilityGroupSelected = () => {
    setIsAssigningCardPermissions(false);
    if (onVisibilityGroupSelected) {
      const cardPermissionCardsData: GetCardPermissionCardsDataResult =
        getCardPermissionCardsData({
          visibleTo,
          selectedGroups,
          selectedCards: new Set([cardId ?? 0]),
          visibilityGroups: allVisibilityGroups,
        });

      onVisibilityGroupSelected(cardId, cardPermissionCardsData);
    }
  };

  const { setReorderMode } = useCardDndToggleContext();

  const hasCardSources = !!sourcesCount;
  const menuItemsObj: {
    duplicateCard: KlueCardMenuItemProps;
    moveCard: KlueCardMenuItemProps;
    moveInCollapsedView: KlueCardMenuItemProps;
    openCard: KlueCardMenuItemProps;
    sources: KlueCardMenuItemProps;
    edit: KlueCardMenuItemProps;
    assignToBattlecards: KlueCardMenuItemProps;
    copyLink: KlueCardMenuItemProps;
    copyEmbed: KlueCardMenuItemProps;
    delete: KlueCardMenuItemProps;
    cardPermissions: KlueCardMenuItemProps;
    copyToBoards: KlueCardMenuItemProps;
    addToDigest: KlueCardMenuItemProps;
    scheduleUpdate: KlueCardMenuItemProps;
  } = {
    openCard: {
      key: 'open-card',
      label: t('Card:header.menu.openCard'),
      icon: <ArrowSquareIn />,
    },
    sources: {
      key: 'sources',
      label: `${t('Card:header.menu.sources')}${
        sourcesCount ? ` (${sourcesCount})` : ''
      }`,
      icon: <Info />,
    },
    edit: {
      key: 'edit',
      label: t('Common:actions.edit'),
      icon: <PencilSimple />,
      separator: true,
    },
    assignToBattlecards: {
      key: 'assign-to-battlecards',
      label: t('Card:header.menu.assignToBattlecards'),
      icon: <StackSimple />,
    },
    cardPermissions: {
      key: 'card-permissions',
      label: t('Card:header.menu.cardPermissions'),
      icon: <Eye />,
      separator: false,
    },
    duplicateCard: {
      key: 'duplicate-card',
      label: t('Card:header.menu.duplicateCard'),
      icon: <Copy />,
      separator: true,
    },
    moveCard: {
      key: 'move-card',
      label: t('Card:header.menu.moveCard'),
      icon: <ArrowsLeftRight />,
      separator: false,
    },
    moveInCollapsedView: {
      key: 'move-in-collapsed-view',
      label: t('Card:header.menu.moveInCollapsedView'),
      icon: <ArrowsLeftRight />,
      separator: false,
    },
    copyToBoards: {
      key: 'copy-to-boards',
      label: t('Card:header.menu.copyToBoards'),
      icon: <CopySimple />,
      separator: false,
    },
    addToDigest: {
      key: 'add-to-digest',
      label: t('Card:header.menu.addToDigest'),
      icon: <EnvelopeSimple />,
      separator: false,
    },
    copyLink: {
      key: 'copy-link',
      label: t('Card:header.menu.copyLink'),
      icon: <Link />,
    },
    copyEmbed: {
      key: 'copy-embed',
      label: t('Card:header.menu.copyEmbed'),
      icon: <Code />,
      separator: view === 'board',
    },
    delete: {
      key: 'delete',
      label: t('Common:actions.delete'),
      icon: <Trash />,
    },
    scheduleUpdate: {
      key: 'schedule-update',
      label: hasSchedule
        ? t('Card:header.menu.editSchedule')
        : t('Card:header.menu.scheduleUpdate'),
      icon: <CalendarBlank />,
      separator: true,
    },
  };
  if (trackingIds) {
    menuItemsObj.copyEmbed.trackingId = trackingIds.copyEmbed;
    menuItemsObj.copyLink.trackingId = trackingIds.copyLink;
    menuItemsObj.duplicateCard.trackingId = trackingIds.duplicateCard;
    menuItemsObj.moveCard.trackingId = trackingIds.moveCard;
    menuItemsObj.moveInCollapsedView.trackingId = trackingIds.moveCard;
    menuItemsObj.copyToBoards.trackingId = trackingIds.copyToBoards;
    menuItemsObj.openCard.trackingId = trackingIds.openCard;
    menuItemsObj.sources.trackingId = trackingIds.sources;
    menuItemsObj.edit.trackingId = trackingIds.edit;
    menuItemsObj.assignToBattlecards.trackingId =
      trackingIds.assignToBattlecards;
    menuItemsObj.cardPermissions.trackingId = trackingIds.cardPermissions;
    menuItemsObj.addToDigest.trackingId = trackingIds.addToDigest;
  }
  // TODO: Refactor to use the order that keys are defined in the menuItemsObj above
  const menuItems = [];
  if (!hideOpenCard && !isCurator) {
    menuItems.push(menuItemsObj.openCard);
  }
  if (isEditable && isCurator) {
    menuItems.push(menuItemsObj.edit);
  }
  if (!hideCardSources) {
    if (isCurator || (!isCurator && hasCardSources)) {
      menuItems.push(menuItemsObj.sources);
    }
  }
  if (isEditable && isCurator && view === 'board') {
    menuItems.push(menuItemsObj.assignToBattlecards);
  }
  // Check if scheduleUpdate will be shown (to determine separator on cardPermissions)
  const willShowScheduleUpdate =
    isEditable &&
    isCurator &&
    isNewCardEditorEnabled &&
    (view === 'board' || view === 'battlecard') &&
    hasSchedule;

  if (
    isEditable &&
    isCurator &&
    isNewCardEditorEnabled &&
    (view === 'board' || view === 'battlecard')
  ) {
    menuItems.push({
      ...menuItemsObj.cardPermissions,
      // Add separator after cardPermissions if scheduleUpdate won't follow
      separator: !willShowScheduleUpdate,
    });
  }
  if (willShowScheduleUpdate) {
    menuItems.push(menuItemsObj.scheduleUpdate);
  }
  if (isEditable) {
    menuItems.push(menuItemsObj.copyLink);
  }
  if (isEditable && isCurator) {
    menuItems.push(menuItemsObj.copyEmbed);
  }
  if (isEditable && isCurator && view === 'board') {
    menuItems.push(menuItemsObj.moveCard);
    menuItems.push(menuItemsObj.moveInCollapsedView);
    if (isCopyToBoardsEnabled && hasSchedule) {
      menuItems.push(menuItemsObj.copyToBoards);
    }
    menuItems.push(menuItemsObj.duplicateCard);
  }
  // Add-to-Digest works on any card regardless of surface — board,
  // battlecard, or search-result menus should all show it as long as the
  // curator is editing.
  if (isEditable && isCurator) {
    menuItems.push(menuItemsObj.addToDigest);
  }
  if (isEditable && isCurator && isNewCardEditorEnabled && view === 'board') {
    menuItems.push(menuItemsObj.delete);
  }

  const logCardInteraction = useCardInteractionLog();
  const handleClickMenuItem = (
    item: ListItemType,
    snowplowLabel: KlueCardSnowPlowLabelType,
  ) => {
    if (cardId) {
      const options: { scrollToSources?: boolean; rank?: number | null } = {
        rank,
      };
      let action: AnalyticsActionType = AnalyticsAction.expand;
      if (item.key === 'sources' && isCurator) {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.viewSources,
          label: snowplowLabel,
          rank,
        });
        return setIsEditingSources(true);
      }
      if (item.key === 'sources' && !isCurator) {
        options.scrollToSources = true;
        action = AnalyticsAction.viewSources;
      }
      if (item.key === 'edit') {
        action = AnalyticsAction.edit;
        logCardInteraction({
          cardId,
          action,
          label: snowplowLabel,
          rank,
        });
        return profileId && redirectToCardEditor({ profileId, cardId });
      }

      if (item.key === 'copy-embed') {
        action = AnalyticsAction.copyEmbed;
        logCardInteraction({
          cardId,
          action,
          label: snowplowLabel,
          rank,
        });
        return handleCopyEmbed();
      }

      if (item.key === 'copy-link') {
        action = AnalyticsAction.copyLink;
        logCardInteraction({
          cardId,
          action,
          label: snowplowLabel,
          rank,
        });
        return handleCopyLink();
      }

      if (item.key === 'duplicate-card') {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.duplicateCard,
          label: snowplowLabel,
          rank,
        });
        return handleDuplicateCard();
      }

      if (item.key === 'move-card') {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.moveCard,
          label: snowplowLabel,
          rank,
        });
        return setIsMovingCard(true);
      }

      if (item.key === 'move-in-collapsed-view') {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.moveCard,
          label: snowplowLabel,
          rank,
        });
        if (profileId) {
          setReorderMode(profileId, true);
        }
        return;
      }

      if (item.key === 'copy-to-boards') {
        setIsCopyingToBoards(true);
        return;
      }

      if (item.key === 'add-to-digest') {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.addToDigest,
          label: snowplowLabel,
          rank,
        });
        setIsAddingToDigest(true);
        return;
      }

      if (item.key === 'delete') {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.removeCard,
          label: snowplowLabel,
          rank,
        });

        setIsDeletingCard(true);

        return;
      }

      if (item.key === 'assign-to-battlecards') {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.assignToBattlecards,
          label: snowplowLabel,
          rank,
        });

        setIsAssigningToBattlecards(true);

        return;
      }

      if (item.key === 'card-permissions') {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.cardPermissions,
          label: snowplowLabel,
          rank,
        });

        // Handle both CardType and SearchResultCardType
        let isDraft = true;
        let allAccess = false;
        let visibilityGroups: Array<{ id: number; name: string }> = [];

        if (card) {
          if ('isDraft' in card) {
            // CardType (camelCase properties)
            isDraft = card.isDraft ?? true;
            allAccess = card.allAccess ?? false;
            visibilityGroups = card.visibilityGroups ?? [];
          } else if ('is_draft' in card) {
            // SearchResultCardType (snake_case properties)
            isDraft = card.is_draft ?? true;
            allAccess = card.all_access ?? false;
            visibilityGroups =
              card.visibility_groups?.map((vg: any) => ({
                id: vg.id ?? vg,
                name: vg.name ?? '',
              })) ?? [];
          }
        }

        const visibleTo = getVisibleToValue({
          isDraft,
          allAccess,
          visibilityGroups,
        });
        setVisibleTo(visibleTo);
        setSelectGroups(visibilityGroups.map((vg) => vg.id));
        setIsAssigningCardPermissions(true);

        return;
      }

      if (item.key === 'schedule-update') {
        logCardInteraction({
          cardId,
          action: AnalyticsAction.scheduleUpdate,
          label: snowplowLabel,
          rank,
        });

        setIsSchedulingUpdate(true);

        return;
      }

      open(cardId, options);
      logCardInteraction({
        cardId,
        action,
        label: snowplowLabel,
        rank,
      });
    }
  };

  return (
    <>
      {isDeletingCard && cardId && (
        <CardDeleteConfirmation
          cardId={cardId}
          onClose={() => setIsDeletingCard(false)}
        />
      )}
      {isMovingCard && cardId && (
        <KlueCardMoveModal
          currentBoardId={card?.board?.id}
          handleClose={() => setIsMovingCard(false)}
          handleSave={handleMoveCard}
        />
      )}
      {isCopyingToBoards && cardId && card && cardFromCardsStore && (
        <CopyToBoardsModal
          onClose={() => setIsCopyingToBoards(false)}
          onCopy={handleCopyToBoards}
          excludeRivalId={sourceRivalId}
          sourcePrompt={cardFromCardsStore.cardSchedule?.prompt ?? ''}
          isPromptRivalSpecific={
            !!(
              sourceRivalName &&
              'cardSchedule' in card &&
              card.cardSchedule?.prompt?.includes(sourceRivalName)
            )
          }
          sourceFrequencyDays={
            cardFromCardsStore.cardSchedule?.frequencyDays ?? null
          }
          sourceResearchMode={
            cardFromCardsStore.cardSchedule?.researchMode ?? true
          }
          sourceIsDraft={cardFromCardsStore.isDraft}
          sourceAllAccess={cardFromCardsStore.allAccess}
          sourceVisibilityGroups={cardFromCardsStore.visibilityGroups.map(
            (g) => g.id,
          )}
        />
      )}
      {isEditingSources && cardId && (
        <KlueCardSourcesModal
          cardId={cardId}
          handleClose={() => setIsEditingSources(false)}
        />
      )}
      {isAssigningToBattlecards && cardId && (
        <KlueCardAssignToBattlecardModal
          id={cardId}
          profileId={profileId}
          onClose={() => setIsAssigningToBattlecards(false)}
        />
      )}
      {isAssigningCardPermissions && cardId && (
        <KlueCardVisibilityGroupsModal
          visibleTo={visibleTo}
          onClose={() => setIsAssigningCardPermissions(false)}
          onDone={handleDoneVisibilityGroupSelected}
        />
      )}
      {isSchedulingUpdate && cardId && (
        <CardScheduleModal
          cardId={cardId}
          onSave={() => setIsSchedulingUpdate(false)}
          onClose={() => setIsSchedulingUpdate(false)}
        />
      )}
      {isAddingToDigest && cardId && (
        <AddToDigestModal
          cardId={cardId}
          contentHtml={card?.data?.textHtml}
          onClose={() => setIsAddingToDigest(false)}
        />
      )}
      {children({ menuItems, handleClickMenuItem })}
    </>
  );
}

type CardDeleteConfirmationProps = {
  cardId: number;
  onClose: () => void;
};

const CardDeleteConfirmation = ({
  cardId,
  onClose,
}: CardDeleteConfirmationProps) => {
  const { t } = useTranslation(['Card']);
  const [deleteCard, deleteState] = useDeleteCardMutation();
  const isDeleting = deleteState.isLoading;
  const { notify } = useNotifier();
  const dispatch = useDispatch();
  function handleSubmit() {
    deleteCard(cardId)
      .unwrap()
      .then(() => {
        dispatch.boards.deleteCards({ cardIds: [cardId] });
        dispatch.cards.delete({ cardIds: [cardId] });
        return notify({
          type: NotifierType.SUCCESS,
          message: t('Card:deleteCard.success'),
          icon: <WarningCircle />,
        });
      })
      .catch((error) => {
        return notify({
          type: NotifierType.ERROR,
          message: t('Card:deleteCard.failure'),
          icon: <WarningCircle />,
        });
      })
      .finally(() => {
        onClose();
      });
  }
  return (
    <Dialog
      title={t('Card:deleteCard.confirmation.heading')}
      description={t(
        `Card:deleteCard.confirmation.${isDeleting ? 'deleting' : 'body'}`,
      )}
      showCancelButton={!isDeleting}
      show
      progressBarProps={
        isDeleting
          ? {
              hideLabel: false,
              min: 0,
              max: 100,
              value: 50,
              state: ProgressBarState.ACTIVE,
              a11yTitle: '',
            }
          : undefined
      }
      showActionButton={!isDeleting}
      action={handleSubmit}
      actionLabel={t('Card:deleteCard.confirmation.confirmButton')}
      position="center"
      onClose={onClose}
      type={DialogType.WARNING}
    />
  );
};

export default KlueCardMenu;
